import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  pageTitle: {
    id: 'category.catalog.page-title',
    defaultMessage: 'Discover our courses',
    description: 'Catalog page title.',
  },
  errorMessage: {
    id: 'catalog.browse.error-message',
    defaultMessage: 'If the problem persists, contact {supportEmail}.',
    description: 'Catalog page error message.',
  },
  backToHome: {
    id: 'catalog.browse.back-to-home',
    defaultMessage: 'Back to home',
    description: 'Back link from the catalog browse page to the home page.',
  },
  eyebrow: {
    id: 'catalog.browse.eyebrow',
    defaultMessage: 'Catalog',
    description: 'Eyebrow label on the catalog browse hero.',
  },
  resultsTitle: {
    id: 'catalog.browse.results-title',
    defaultMessage: 'Results for "{query}"',
    description: 'Catalog browse hero title when a search query is active.',
  },
  heroDescription: {
    id: 'catalog.browse.hero-description',
    defaultMessage: 'Explore training designed for clear progression, concrete skills, and lasting growth.',
    description: 'Description text in the catalog browse hero.',
  },
  availableCoursesLabel: {
    id: 'catalog.browse.available-courses-label',
    defaultMessage: 'courses available',
    description: 'Status label showing the number of available courses.',
  },
  heroStatusDescription: {
    id: 'catalog.browse.hero-status-description',
    defaultMessage: 'An editorial selection built for skill growth, clarity, and professional impact.',
    description: 'Supporting text in the catalog browse hero status card.',
  },
  searchInputPlaceholder: {
    id: 'catalog.browse.search-input-placeholder',
    defaultMessage: 'Search for a course, skill, or topic...',
    description: 'Search input placeholder on the catalog browse page.',
  },
  searchInputAriaLabel: {
    id: 'catalog.browse.search-input-aria-label',
    defaultMessage: 'Search for a course',
    description: 'Search input aria label on the catalog browse page.',
  },
  searchButton: {
    id: 'catalog.browse.search-button',
    defaultMessage: 'Search',
    description: 'Search submit button label on the catalog browse page.',
  },
  filtersEyebrow: {
    id: 'catalog.browse.filters-eyebrow',
    defaultMessage: 'Filters',
    description: 'Eyebrow label for the filters card.',
  },
  filtersTitle: {
    id: 'catalog.browse.filters-title',
    defaultMessage: 'Refine selection',
    description: 'Title for the filters card.',
  },
  filtersSummary: {
    id: 'catalog.browse.filters-summary',
    defaultMessage: 'Filter by organization or language to find the right course faster.',
    description: 'Summary text for the filters card.',
  },
  resetFilters: {
    id: 'catalog.browse.reset-filters',
    defaultMessage: 'Reset',
    description: 'Label for resetting active browse filters.',
  },
  organizationsTitle: {
    id: 'catalog.browse.organizations-title',
    defaultMessage: 'Organizations',
    description: 'Title for the organizations filter group.',
  },
  languagesTitle: {
    id: 'catalog.browse.languages-title',
    defaultMessage: 'Languages',
    description: 'Title for the languages filter group.',
  },
  resultsEyebrowSearch: {
    id: 'catalog.browse.results-eyebrow-search',
    defaultMessage: 'Search',
    description: 'Eyebrow label when search results are displayed.',
  },
  resultsEyebrowDefault: {
    id: 'catalog.browse.results-eyebrow-default',
    defaultMessage: 'Selection',
    description: 'Eyebrow label when the default course selection is displayed.',
  },
  resultsHeadingSearch: {
    id: 'catalog.browse.results-heading-search',
    defaultMessage: 'Search results',
    description: 'Results header title when a search is active.',
  },
  resultsHeadingDefault: {
    id: 'catalog.browse.results-heading-default',
    defaultMessage: 'All our courses',
    description: 'Results header title when no search is active.',
  },
  resultsSummary: {
    id: 'catalog.browse.results-summary',
    defaultMessage: '{first} - {last} of {total} courses',
    description: 'Summary text for current search results range.',
  },
  noSelectionMatch: {
    id: 'catalog.browse.no-selection-match',
    defaultMessage: 'No training currently matches this selection.',
    description: 'Summary text when no result matches the active selection.',
  },
  refreshingStatus: {
    id: 'catalog.browse.refreshing-status',
    defaultMessage: 'Updating...',
    description: 'Status text displayed while search results refresh.',
  },
  resultsCountStatus: {
    id: 'catalog.browse.results-count-status',
    defaultMessage: '{total} courses',
    description: 'Status text showing the total number of courses.',
  },
  activeSearchTag: {
    id: 'catalog.browse.active-search-tag',
    defaultMessage: 'Search: {query}',
    description: 'Active filter tag for the current search query.',
  },
  errorTitle: {
    id: 'catalog.browse.error-title',
    defaultMessage: 'Unable to load catalog',
    description: 'Error title on the catalog browse page.',
  },
  emptyStateTitle: {
    id: 'catalog.browse.empty-state-title',
    defaultMessage: 'No courses found',
    description: 'Empty state title on the catalog browse page.',
  },
  emptyStateDescription: {
    id: 'catalog.browse.empty-state-description',
    defaultMessage: 'Try another keyword or remove some filters to broaden the selection.',
    description: 'Empty state description on the catalog browse page.',
  },
  resetSearch: {
    id: 'catalog.browse.reset-search',
    defaultMessage: 'Reset search',
    description: 'Label for the button that clears the current search and filters.',
  },
  previousPage: {
    id: 'catalog.browse.previous-page',
    defaultMessage: 'Previous',
    description: 'Label for the previous page button.',
  },
  paginationSummary: {
    id: 'catalog.browse.pagination-summary',
    defaultMessage: 'Page {current} of {total}',
    description: 'Pagination summary label.',
  },
  nextPage: {
    id: 'catalog.browse.next-page',
    defaultMessage: 'Next',
    description: 'Label for the next page button.',
  },
  noCoursesAvailable: {
    id: 'catalog.browse.no-courses-available',
    defaultMessage: 'No courses available',
    description: 'No courses available alert title.',
  },
  noCoursesAvailableMessage: {
    id: 'catalog.browse.no-courses-available-message',
    defaultMessage: 'No courses are available in the catalog right now. Come back later to discover upcoming training.',
    description: 'No courses available alert message.',
  },
  searchPlaceholder: {
    id: 'category.catalog.search-placeholder',
    defaultMessage: 'Search for a course, skill, or topic...',
    description: 'Search placeholder.',
  },
  searchResults: {
    id: 'category.catalog.search-results',
    defaultMessage: 'Results for "{query}"',
    description: 'Search results heading.',
  },
  noSearchResults: {
    id: 'category.catalog.no-search-results',
    defaultMessage: 'No results found for "{query}"',
    description: 'No search results.',
  },
  exploreCourses: {
    id: 'category.catalog.explore-courses',
    defaultMessage: 'Explore courses',
    description: 'Explore courses.',
  },
  noResultsFound: {
    id: 'category.catalog.no-results-found',
    defaultMessage: 'No results found',
    description: 'No results found.',
  },
  organizations: {
    id: 'category.catalog.filter.organizations',
    defaultMessage: 'Organizations',
    description: 'Organizations filter.',
  },
  languages: {
    id: 'category.catalog.filter.languages',
    defaultMessage: 'Languages',
    description: 'Languages filter.',
  },
  courseTypes: {
    id: 'category.catalog.filter.course-types',
    defaultMessage: 'Course types',
    description: 'Course types filter.',
  },
});

export default messages;
